import React, { useState, useMemo, useEffect, useCallback } from 'react'
import PropTypes from 'prop-types'
import { useLazyQuery, useMutation } from '@apollo/client'
import { Modal, Radio, Select, Button, Alert, message } from 'antd'
import _ from 'lodash'

import CustomerFormContainer from '../CustomerFormContainer'
import { formatMutationErrors } from './format'
import {
    CONTACT_TYPEAHEAD_QUERY,
    UPDATE_CONTACT_COMPANIES_MUTATION
} from './queries'

const EXISTING = 'existing'
const NEW = 'new'

const contactLabel = (c) =>
    [c.fullName || c.customerNo, c.phoneNumber, c.email].filter(Boolean).join(' · ')

// Modal for attaching a contact to a company from the dashboard search view.
// Two modes: pick an existing customer (union their companies + this one) or
// create a new customer already linked to the company.
const AddContactModal = ({ company, visible, onClose, onSuccess }) => {
    const [mode, setMode] = useState(EXISTING)
    const [selected, setSelected] = useState(null)
    // The chosen contact node, captured on select so submit doesn't depend on
    // the option still being present in the (search-driven) options list.
    const [selectedNode, setSelectedNode] = useState(null)

    const companyId = company && company.id

    const [runSearch, search] = useLazyQuery(CONTACT_TYPEAHEAD_QUERY, {
        fetchPolicy: 'network-only'
    })

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const debouncedSearch = useMemo(
        () => _.debounce((q) => { if (q.trim()) runSearch({ variables: { query: q.trim() } }) }, 350),
        [runSearch]
    )

    // Cancel any pending debounced search when the modal unmounts (destroyOnClose)
    // so it can't fire against a torn-down component.
    useEffect(() => () => debouncedSearch.cancel(), [debouncedSearch])

    const options = useMemo(() => {
        const nodes = (search.data && search.data.searchCustomers && search.data.searchCustomers.nodes) || []
        return nodes.map((n) => ({ value: n.customerNo, label: contactLabel(n), node: n }))
    }, [search.data])

    const [updateContact, updateState] = useMutation(UPDATE_CONTACT_COMPANIES_MUTATION)
    const submitting = updateState.loading

    const reset = useCallback(() => {
        setMode(EXISTING)
        setSelected(null)
        setSelectedNode(null)
    }, [])

    const close = () => { reset(); onClose() }

    const finish = (customer) => {
        message.success(`เพิ่มผู้ติดต่อ ${customer.fullName || customer.customerNo} เข้าบริษัทแล้ว`)
        reset()
        onSuccess(customer)
        onClose()
    }

    const handleAddExisting = async () => {
        if (!selectedNode) return
        const existingIds = (selectedNode.companies || []).map((c) => c.id)
        if (existingIds.includes(companyId)) {
            message.info('ผู้ติดต่อนี้อยู่ในบริษัทนี้อยู่แล้ว')
            return
        }
        try {
            const { data } = await updateContact({
                variables: {
                    customerNo: selectedNode.customerNo,
                    customerParams: { customerCompanyIds: _.uniq([...existingIds, companyId]) }
                }
            })
            const payload = data && data.updateCustomer
            if (!payload) { message.error('ไม่สามารถเพิ่มผู้ติดต่อได้'); return }
            if (payload.errors) { message.error(formatMutationErrors(payload.errors)); return }
            finish(payload.customer)
        } catch (e) {
            // Network/GraphQL error already surfaces via updateState.error Alert.
        }
    }

    return (
        <Modal
            title={`เพิ่มผู้ติดต่อ${company ? ` · ${company.name}` : ''}`}
            open={visible}
            onCancel={close}
            footer={false}
            destroyOnClose
            width={mode === NEW ? 900 : 560}
        >
            {updateState.error && (
                <Alert type="error" style={{ marginBottom: 16 }} message={updateState.error.message} />
            )}

            <Radio.Group
                value={mode}
                onChange={(e) => setMode(e.target.value)}
                optionType="button"
                buttonStyle="solid"
                style={{ marginBottom: 20 }}
            >
                <Radio.Button value={EXISTING}>เลือกจากลูกค้าที่มีอยู่</Radio.Button>
                <Radio.Button value={NEW}>สร้างผู้ติดต่อใหม่</Radio.Button>
            </Radio.Group>

            {mode === EXISTING ? (
                <div>
                    <Select
                        showSearch
                        allowClear
                        filterOption={false}
                        value={selected}
                        onSearch={debouncedSearch}
                        onChange={(val, option) => { setSelected(val); setSelectedNode(option ? option.node : null) }}
                        notFoundContent={search.loading ? 'กำลังค้นหา...' : 'พิมพ์เพื่อค้นหาลูกค้า'}
                        placeholder="ค้นหาชื่อ, เบอร์โทร, อีเมล"
                        options={options}
                        loading={search.loading}
                        style={{ width: '100%' }}
                    />
                    <div style={{ textAlign: 'right', marginTop: 20 }}>
                        <Button onClick={close} style={{ marginRight: 8 }}>ยกเลิก</Button>
                        <Button
                            type="primary"
                            disabled={!selected}
                            loading={submitting}
                            onClick={handleAddExisting}
                        >
                            เพิ่มเข้าบริษัท
                        </Button>
                    </div>
                </div>
            ) : (
                // Reuse the full customer-create form, with this company pre-selected.
                // Its existing save flow persists the customer (already linked to the
                // company) and redirects to the new customer's page.
                <CustomerFormContainer embedded defaultCompany={company} />
            )}
        </Modal>
    )
}

AddContactModal.propTypes = {
    company: PropTypes.object,
    visible: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    onSuccess: PropTypes.func
}

AddContactModal.defaultProps = {
    company: null,
    onSuccess: () => {}
}

export default AddContactModal
